import {
	Container,
	ContainerPadding,
	Text,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { oneformBFFNdpsSend } from 'src/bff';
import {
	DetalleMotivo,
	LegalFullScreenSinConsentmientos,
	MultipleOptionCTAs,
} from 'src/components';
import {
	FormController,
	LayoutTemplate,
	useOneFormContext,
} from 'src/feature-app';
import { formDataBuilder } from 'src/forms/format-fields';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import {
	ConfirmacionNoRenovacion,
	FakeTriggerCTA,
	ThankYouNoRenovacion,
} from '.';
import {
	addRemovedStep,
	getIndexInSteps,
	removeStep,
	resetStepsScreenIndex,
} from '../helpers/add-remove-steps';
import { getCommonFormData } from './FormData';
import { NoRenovacionFormProps } from './NoRenovacionController';
import { useInitializeNoRenovacionForm } from './use-initialize-no-renovacion-form';

export function NoRenovacionNoQuiero({
	dpsData,
	idLead,
	idVehicle,
	idPerson,
	trackFormLoad,
	handleError,
	estado,
}: NoRenovacionFormProps) {
	const { initializeForm, handleNextStep } = useOneFormContext();
	const store = useStore();
	const dispatch = useDispatch();
	const { formData } = useSelector((state: OneFormState) => state);
	const [presubmitSent, setPresubmitSent] = useState(false);
	const { razones, formasMovilidad } = formData.fields;

	const CTAsFormasMovilidad = [
		{
			label: 'Coche compartido (car sharing)',
			value: '1',
		},
		{
			label: 'Moto compartida (moto sharing)',
			value: '2',
		},
		{
			label: 'Transporte público',
			value: '3',
		},
		{
			label: 'Patinete',
			value: '4',
		},
		{
			label: 'Moto',
			value: '5',
		},
		{
			label: 'Bicicleta',
			value: '6',
		},
		{
			label: 'Otros',
			value: '8', // Si este valor cambia se tiene que revisar handleDetalleFormasMovilidad()
		},
	];

	const CTAsFormasMovilidadExclusive = [
		{
			label: 'No tengo necesidad de utilizar ningún medio de transporte para desplazarme',
			value: '7',
		},
	];

	const CTAsRazones = [
		{
			label: 'Económicas',
			value: '1',
		},
		{
			label: 'Medioambientales',
			value: '2',
		},
		{
			label: 'Ahorro de tiempo',
			value: '3',
		},
		{
			label: 'Otras razones',
			value: '4', // Si este valor cambia se tiene que revisar handleDetalleRazones()
		},
	];

	let index = 0;
	const steps: Steps[] = [
		{
			title: (
				<>
					<Text bold>
						Nos hubiese encantado seguir siendo tu compañero de
						viaje.{' '}
					</Text>
					¿Nos cuentas el porqué de este cambio por si te podemos
					ayudar en un futuro?
				</>
			),
			fields: <LayoutTemplate element={<FakeTriggerCTA />} />,
			screenIndex: index++,
			name: 'introduccion',
		},
		{
			title: (
				<>
					<Text bold>
						¿Cómo vas a realizar tus desplazamientos de ahora en{' '}
						<span style={{ whiteSpace: 'nowrap' }}>
							adelante?
							<sup style={{ top: 'auto', fontSize: '100%' }}>
								*
							</sup>
						</span>
					</Text>
					<Container padding={{ top: ContainerPadding.dynamic0020 }}>
						<Text
							appearance={TokenTextAppearance.copy200}
							staticSize
						>
							Puedes elegir más de una opción
						</Text>
					</Container>
				</>
			),
			alternateTitle: (
				<>
					<Text bold>
						¿Cómo vas a realizar tus desplazamientos de ahora en{' '}
						<span style={{ whiteSpace: 'nowrap' }}>
							adelante?
							<sup style={{ top: 'auto', fontSize: '100%' }}>
								*
							</sup>
						</span>
					</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<MultipleOptionCTAs
							ignoreHandleClick={true}
							ctaValues={CTAsFormasMovilidad}
							minSelected={1}
							name="formasMovilidad"
							exclusiveCTA={true}
							exclusiveCTAValues={CTAsFormasMovilidadExclusive}
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'formasMovilidad',
			outputs: ['formasMovilidad'],
			outputsText: ['formasMovilidad'],
			ctas: [...CTAsFormasMovilidad, ...CTAsFormasMovilidadExclusive],
		},
		{
			title: (
				<>
					¿Nos explicas en{' '}
					<Text bold>
						qué otro medio te vas a{' '}
						<span style={{ whiteSpace: 'nowrap' }}>
							desplazar?
							<sup style={{ top: 'auto', fontSize: '100%' }}>
								*
							</sup>
						</span>
					</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<DetalleMotivo
							CTALabel="No, eso es todo"
							name="detalleFormasMovilidad"
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'detalleFormasMovilidad',
			outputs: ['detalleFormasMovilidad'],
			outputsText: ['detalleFormasMovilidad'],
		},
		{
			title: (
				<>
					<Text bold>¿Cuáles han sido las razones </Text>
					para hacer este{' '}
					<span style={{ whiteSpace: 'nowrap' }}>
						cambio?
						<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
					</span>
					<Container padding={{ top: ContainerPadding.dynamic0020 }}>
						<Text
							appearance={TokenTextAppearance.copy200}
							staticSize
						>
							Puedes elegir más de una opción
						</Text>
					</Container>
				</>
			),
			alternateTitle: (
				<>
					<Text bold>¿Cuáles han sido las razones </Text>
					para hacer este{' '}
					<span style={{ whiteSpace: 'nowrap' }}>
						cambio?
						<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
					</span>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<MultipleOptionCTAs
							ignoreHandleClick={true}
							ctaValues={CTAsRazones}
							minSelected={1}
							name="razones"
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'razones',
			outputs: ['razones'],
			outputsText: ['razones'],
			ctas: CTAsRazones,
		},
		{
			title: (
				<>
					<Text bold>
						¿Puedes{' '}
						<span style={{ whiteSpace: 'nowrap' }}>
							compartirlas?
							<sup style={{ top: 'auto', fontSize: '100%' }}>
								*
							</sup>
						</span>
					</Text>
				</>
			),
			fields: (
				<LayoutTemplate
					element={
						<DetalleMotivo
							CTALabel="No, eso es todo"
							name="detalleRazones"
						/>
					}
				/>
			),
			screenIndex: index++,
			name: 'detalleRazones',
			outputs: ['detalleRazones'],
			outputsText: ['detalleRazones'],
		},
		{
			fields: <ConfirmacionNoRenovacion idPerson={idPerson} />,
			screenIndex: index++,
			name: 'Confirmacion',
			outputs: ['confirmacion'],
			outputsText: ['confirmacion'],
		},
	];

	useEffect(() => {
		if (formasMovilidad) handleDetalleFormasMovilidad();
	}, [formasMovilidad]);

	const handleDetalleFormasMovilidad = () => {
		const { steps: actualSteps, screenIndex } = store.getState().formInfo;
		let newSteps = [...actualSteps];

		if (formasMovilidad.includes('8')) {
			const stepIndex = getIndexInSteps(
				newSteps,
				'detalleFormasMovilidad',
			);

			if (stepIndex === -1) {
				newSteps = addRemovedStep(
					steps,
					actualSteps,
					'detalleFormasMovilidad',
					screenIndex,
				);
				setTimeout(() => {
					handleNextStep();
				}, 0);
			}
		} else {
			newSteps = removeStep(newSteps, 'detalleFormasMovilidad');
		}

		const newStepsWithIndex = resetStepsScreenIndex(newSteps);

		dispatch({ type: 'UPDATE_STEPS', payload: newStepsWithIndex });
		dispatch({
			type: 'UPDATE_LAST_STEP',
			payload: newStepsWithIndex[newStepsWithIndex.length - 1],
		});
		dispatch({
			type: 'SET_NUMBER_OF_SCREENS',
			payload: newStepsWithIndex.length,
		});
	};

	useEffect(() => {
		if (razones) handleDetalleRazones();
	}, [razones]);

	const handleDetalleRazones = () => {
		const { steps: actualSteps, screenIndex } = store.getState().formInfo;
		let newSteps = [...actualSteps];

		if (razones.includes('4')) {
			const stepIndex = getIndexInSteps(newSteps, 'detalleRazones');

			if (stepIndex === -1) {
				newSteps = addRemovedStep(
					steps,
					actualSteps,
					'detalleRazones',
					screenIndex,
				);
				setTimeout(() => {
					handleNextStep();
				}, 0);
			}
		} else {
			newSteps = removeStep(newSteps, 'detalleRazones');
		}

		const newStepsWithIndex = resetStepsScreenIndex(newSteps);

		dispatch({ type: 'UPDATE_STEPS', payload: newStepsWithIndex });
		dispatch({
			type: 'UPDATE_LAST_STEP',
			payload: newStepsWithIndex[newStepsWithIndex.length - 1],
		});
		dispatch({
			type: 'SET_NUMBER_OF_SCREENS',
			payload: newStepsWithIndex.length,
		});
	};

	useEffect(() => {
		if (!presubmitSent) sendForm(true);
	}, []);

	const NuevoFormData: Partial<FormDataGroup> = {
		fields: {
			formName: 'no_renovacion-no_quiero',
			formType: 'otherform',
			pageCategory: 'Encuesta',
		}, // Analítica
	};

	const getFormInfo = (): {
		firstStep: Steps;
		lastStep: Steps;
		stepsHistory: number[];
		numberOfScreens: number;
		newSteps?: Steps[];
	} => {
		return {
			firstStep: steps[0],
			lastStep: steps[steps.length - 1],
			stepsHistory: [0],
			numberOfScreens: steps.length,
			newSteps: null,
		};
	};

	const generateData = (
		isPresubmit: boolean,
		data?: Partial<FormDataGroup>,
	) => {
		const { formData }: OneFormState = store.getState();
		const { fields } = formData;

		const submitData = !isPresubmit
			? {
					'fields[SURVEY_ANSWER_07]': orderString(
						fields.formasMovilidad,
					),
					'fields[SURVEY_ANSWER_08]': fields.detalleFormasMovilidad,
					'fields[SURVEY_ANSWER_09]': orderString(fields.razones),
					'fields[SURVEY_ANSWER_10]': fields.detalleRazones,
			  }
			: null;

		return {
			...getCommonFormData(
				isPresubmit,
				idPerson,
				idVehicle,
				dpsData,
				fields,
				data,
				'2',
			),
			...submitData,
		};
	};

	const orderString = orderMotivos => {
		if (orderMotivos == null) {
			return null;
		}

		const array = orderMotivos.split(',');

		const sortedArray = array.sort((a, b) => {
			return parseInt(a) - parseInt(b);
		});

		const orderString = sortedArray.join(',');

		return orderString;
	};

	const sendForm = async (isPresubmit?: boolean): Promise<boolean> => {
		return new Promise<any>(async (resolve, reject) => {
			const data = generateData(isPresubmit, NuevoFormData);
			const dataStringified = JSON.stringify(data);
			const dataParsed = JSON.parse(dataStringified);
			const formData = formDataBuilder(dataParsed);
			const response = await oneformBFFNdpsSend(formData);

			if (response?.data?.status?.code == 200) {
				if (isPresubmit) setPresubmitSent(true);
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				resolve(true);
			} else {
				if (isPresubmit) handleError();
				resolve(false);
			}
		});
	};

	const { firstStep, lastStep, loading } =
		useInitializeNoRenovacionForm(steps);

	useEffect(() => {
		const NuevoFormInfo: Partial<FormInfo> = {
			formStarted: true,
			sendFormCallback: sendForm,
			fullScreen: firstStep.fullScreen ? true : false,
			lastStep,
			numberOfScreens: steps.length,
			screenIndex: firstStep.screenIndex,
			formTheme: 'main',
			notALayer: true,
			formType: 'inPage',
			step: firstStep,
			steps,
			stepsHistory: [0],
			customLegal:
				idPerson.LSSI__c === 'PERMITE' ? (
					<LegalFullScreenSinConsentmientos />
				) : null,
			hideLegal: idPerson.LSSI__c === 'PERMITE' ? true : false,
			thankYouComponent: <ThankYouNoRenovacion />,
		};

		if (!loading) {
			initializeForm(NuevoFormInfo, NuevoFormData, true);
			trackFormLoad();
		}
	}, [loading]);

	return presubmitSent && steps ? (
		<FormController steps={steps} screenType="full-screen" />
	) : null;
}
